// src/app/modules/Members/pages/components/EditMemberModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as actions from '../../../Dashbord/_redux/dashboard/Actions';
import { X, Save, Shield } from 'lucide-react';
import './EditMemberModal.scss';

export function EditMemberModal({ show, onHide, member, onSuccess }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    role: '',
    permissions: [],
    status: '',
  });
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (member && show) {
      setFormData({
        role: member.role || '',
        permissions: member.permissions || [],
        status: member.status || 'active',
      });
    }
  }, [member, show]);

  useEffect(() => {
    if (!show) {
      setFormData({
        role: '',
        permissions: [],
        status: '',
      });
      setLoading(false);
    }
  }, [show]);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function handlePermissionToggle(permission) {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!isMountedRef.current) return;
    
    setLoading(true);

    try {
      await dispatch(actions.addData('update_member', {
        membership_id: member.membership_id,
        updates: formData,
      }));
      
      if (isMountedRef.current) {
        onHide();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error updating member:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }

  const availablePermissions = [
    { value: 'manage_organization', label: 'Manage Organization', description: 'Edit organization settings' },
    { value: 'manage_users', label: 'Manage Users', description: 'Add, edit, and remove members' },
    { value: 'generate_codes', label: 'Generate Codes', description: 'Create access codes' },
    { value: 'validate_codes', label: 'Validate Codes', description: 'Check and validate access codes' },
    { value: 'view_logs', label: 'View Logs', description: 'Access activity and access logs' },
    { value: 'manage_access', label: 'Manage Access', description: 'Control access permissions' },
    { value: 'emergency_response', label: 'Emergency Response', description: 'Handle emergency situations' },
    { value: 'view_analytics', label: 'View Analytics', description: 'Access reports and analytics' },
    { value: 'manage_blacklist', label: 'Manage Blacklist', description: 'Manage visitor blacklist' },
  ];

  if (!member) return null;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      size="lg"
      className="edit-member-modal"
    >
      <div className="edit-member-content">
        <div className="modal-header-custom">
          <h3>Edit Member</h3>
          <button className="btn-close-custom" onClick={onHide} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body-custom">
            {/* Member Info Display */}
            <div className="member-info-display mb-4">
              <div className="member-name">
                {member.user?.first_name} {member.user?.last_name}
              </div>
              <div className="member-email">{member.user?.email}</div>
            </div>

            <div className="form-group">
              <label>
                <Shield size={16} className="mr-2" />
                Role *
              </label>
              <select
                name="role"
                className="form-control"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="member">Member</option>
                <option value="staff">Staff</option>
                <option value="security">Security</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                className="form-control"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="form-group">
              <label className="mb-3">Permissions</label>
              <div className="permissions-list">
                {availablePermissions.map((perm) => (
                  <div key={perm.value} className="permission-item">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(perm.value)}
                        onChange={() => handlePermissionToggle(perm.value)}
                      />
                      <span className="checkmark"></span>
                      <div className="permission-info">
                        <div className="permission-label">{perm.label}</div>
                        <div className="permission-description">{perm.description}</div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="modal-footer-custom">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={onHide}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-submit" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}